import { useCallback } from 'react';
import { usePhotoContext } from '../contexts/PhotoContext';
import { Photo } from '../types/photo';

const MAX_PHOTOS = 20;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

/**
 * Upload une image vers le serveur
 * @param file - Le fichier à uploader
 * @returns L'URL de l'image uploadée
 */
const uploadImageToServer = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('photo', file);

  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api'}/properties/upload-photo`, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Erreur lors de l\'upload de l\'image');
  }

  const result = await response.json();
  
  // Construire l'URL complète de l'image
  const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
  const fullUrl = `${baseUrl.replace('/api', '')}${result.url}`;
  
  console.log('✅ Image uploadée avec succès:', fullUrl);
  return fullUrl;
};

export const usePhotoUpload = () => {
  const { photos, addPhotos, updatePhotoProgress, updatePhoto } = usePhotoContext();

  const validateFiles = useCallback((files: File[]) => {
    if (photos.length + files.length > MAX_PHOTOS) {
      throw new Error(`Vous ne pouvez pas ajouter plus de ${MAX_PHOTOS} photos`);
    }

    files.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        throw new Error(`Le fichier ${file.name} dépasse la taille maximale de 5MB`);
      }
    });
  }, [photos.length]);

  const processFiles = useCallback(async (acceptedFiles: File[]) => {
    const newPhotos: Photo[] = acceptedFiles.map(file => ({
      id: crypto.randomUUID(),
      file,
      preview: URL.createObjectURL(file), // Pour l'affichage dans le formulaire
      progress: 0,
      forWeb: true
    }));

    addPhotos(newPhotos);

    // Upload réel vers le serveur
    for (const photo of newPhotos) {
      try {
        console.log(`🚀 Upload de la photo ${photo.id} en cours...`);
        
        // Simuler la progression pendant l'upload
        updatePhotoProgress(photo.id, 10);
        
        // Upload vers le serveur
        const serverUrl = await uploadImageToServer(photo.file);
        
        // Mettre à jour la photo existante avec l'URL du serveur
        updatePhoto(photo.id, {
          preview: serverUrl, // Remplacer l'URL temporaire par l'URL du serveur
          serverUrl: serverUrl,
          progress: 100
        });
        
        console.log(`✅ Photo ${photo.id} uploadée avec succès:`, serverUrl);
        
      } catch (error) {
        console.error(`❌ Erreur lors de l'upload de la photo ${photo.id}:`, error);
        
        // Marquer la photo existante comme ayant une erreur
        updatePhoto(photo.id, {
          progress: 0,
          error: error instanceof Error ? error.message : 'Erreur d\'upload'
        });
      }
    }
  }, [addPhotos, updatePhotoProgress, photos]);

  const handleUpload = useCallback(async (files: File[]) => {
    try {
      validateFiles(files);
      await processFiles(files);
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : 'Une erreur est survenue';
    }
  }, [validateFiles, processFiles]);

  return { handleUpload };
};